import React, {
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Button,
  Icon,
  Input,
  Switch,
} from 'antd';

import defaultTestimonialAvatarImage from '../../styles/images/no_testimonial_avatar_image.png';
import i18n from '../../config/i18n';
import {
  DISABLED,
  ENABLED,
} from '../../constants/Status';

const AdminTestimonialTableRow = ({
  actions: {
    createSuccessAlert,
    createWarningAlert,
    createAlert,
    deleteTestimonialSuccess,
    updateTestimonialSuccess,
  },
  callbacks: {
    deleteTestimonialCallback,
    updateTestimonialCallback,
  },
  medicalGroup: { name: medicalGroupName = '' },
  onClickAvatar,
  specialty: { name: specialtyName = '' },
  testimonial: {
    id,
    status: initialStatus,
    user: {
      firstName,
      lastName,
      email,
    },
    userId,
    websiteURL,
    description,
    avatar,
  },
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [status, setStatus] = useState(initialStatus);
  const websiteUrlInputRef = useRef();
  const descriptionInputRef = useRef();

  const handleUpdate = async (e) => {
    e.preventDefault();

    const newWebsiteURL = websiteUrlInputRef.current.state.value;
    const newDescription = descriptionInputRef.current.state.value;

    // allows empty websiteURL
    if (newWebsiteURL !== '') {
      if (!newWebsiteURL.startsWith('http://') && !newWebsiteURL.startsWith('https://')) {
        createWarningAlert(i18n.t('urlShouldStartWith'));
        return;
      }
      if (newWebsiteURL.includes('http://', 7) || newWebsiteURL.includes('https://', 7)) {
        createWarningAlert(i18n.t('urlRepeatWith'));
        return;
      }
    }

    // NOTE: testimonial description should have a character count limitation.
    // if (newDescription.length > 140) {
    //   createWarningAlert('The character counts of of the description exceed 140 characters.');
    //   return;
    // }

    // NOTE: we need to include "userId" into payload which is required
    // by the testimonial endpoint.
    const newValue = {
      userId,
      websiteURL: newWebsiteURL,
      description: newDescription,
    };

    setIsUpdating(true);
    try {
      const data = await updateTestimonialCallback(id, newValue);
      createSuccessAlert(i18n.t('updateSuccess'));
      updateTestimonialSuccess(id, data);
    } catch (err) {
      createAlert(err);
      websiteUrlInputRef.current = websiteURL;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();

    // eslint-disable-next-line no-alert
    if (window.confirm(i18n.t('deleteTestimonialWarningMsg'))) {
      setIsDeleting(true);
      try {
        await deleteTestimonialCallback(id);
        createSuccessAlert(i18n.t('deleteSuccess'));
        deleteTestimonialSuccess(id);
      } catch (err) {
        createAlert(err);
        setIsDeleting(false);
      }
    }
  };

  const handleSwitchChange = async (checked, e) => {
    e.preventDefault();

    const newStatus = status === DISABLED ? ENABLED : DISABLED;

    // NOTE: we need to include "userId" into payload which is required
    // by the testimonial endpoint.
    const newValue = {
      userId,
      status: newStatus,
    };

    try {
      await updateTestimonialCallback(id, newValue);
      createSuccessAlert(i18n.t('updateSuccess'));
      setStatus(newStatus);
    } catch (err) {
      createAlert(err);
    }
  };

  return {
    key: id,
    avatar: (
      <div style={{ position: 'relative' }}>
        <Avatar
          alt={i18n.t('testimonialAvatar')}
          onClick={() => onClickAvatar({ id, userId })}
          size={60}
          src={defaultTestimonialAvatarImage}
          srcSet={avatar}
          style={{ cursor: 'pointer' }}
        />
        <div>
          <Icon
            onClick={() => onClickAvatar({ id, userId })}
            type="camera"
            theme="filled"
            style={{ position: 'absolute', top: '36px', left: '39px', fontSize: '21px' }}
          />
        </div>
      </div>
    ),
    name: `${firstName} ${lastName}`,
    email,
    specialty: specialtyName,
    facility: medicalGroupName,
    webURL: (
      <Input.TextArea
        defaultValue={websiteURL || ''}
        ref={websiteUrlInputRef}
        style={{ width: '180px' }}
      />
    ),
    description: (
      <Input.TextArea
        defaultValue={description || ''}
        ref={descriptionInputRef}
        style={{ width: '180px' }}
      />
    ),
    actions: (
      <Button.Group style={{ whiteSpace: 'nowrap' }}>
        <Button
          type="primary"
          onClick={handleUpdate}
          disabled={isUpdating || isDeleting}
          ghost
        >
          {i18n.t(isUpdating ? 'updating' : 'update')}
        </Button>
        <Button
          type="danger"
          onClick={handleDelete}
          disabled={isUpdating || isDeleting}
          ghost
        >
          {i18n.t(isDeleting ? 'deleting' : 'delete')}
        </Button>
      </Button.Group>
    ),
    onLandingPage: (
      <Switch
        checked={status === ENABLED}
        onChange={handleSwitchChange}
      />
    ),
  };
};

/*
 * NOTE: when new user register, his/her default medicalGroup and specialty
 * won't be set initially.
 */
const metadataShape = PropTypes.shape({
  id: PropTypes.number,
  code: PropTypes.string,
  description: PropTypes.string,
  name: PropTypes.string,
});

AdminTestimonialTableRow.propTypes = {
  testimonial: PropTypes.shape({
    id: PropTypes.number.isRequired,
    websiteURL: PropTypes.string,
    user: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
    }),
  }).isRequired,
  medicalGroup: metadataShape,
  specialty: metadataShape,
  callbacks: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

AdminTestimonialTableRow.defaultProps = {
  medicalGroup: {},
  specialty: {},
};

export default AdminTestimonialTableRow;



// WEBPACK FOOTER //
// ./src/components/testimonial/AdminTestimonialTableRow.js